import React, { useEffect, useState } from 'react';
import { insiderOwnershipApi } from '../services/api';

interface InsiderHolding {
  ticker: string;
  insider: string;
  relationship: string;
  balance: number;
  designation: string;
}

interface InstitutionalHolding {
  filerName: string;
  reportDate: string | null;
  filingQuarter: string | null;
  nameOfIssuer: string | null;
  designation: string | null;
  shares: number;
  type: string | null;
}

interface SearchData {
  ticker: string;
  per10owners: InsiderHolding[];
  directors: InsiderHolding[];
  officers: InsiderHolding[];
  issuer: InsiderHolding[];
  institutions: InstitutionalHolding[];
  totalInstitutionalShares: number;
}

interface ParsedHolding {
  ticker: string;
  name: string;
  insider: string;
  relationship: string;
  statusChange: string;
  date: string;
  designation: string;
  holder: string;
  balance: number;
  calculated: number;
  equivalent: number;
}

type TabKey = 'search' | 'upload';
const TABS: { key: TabKey; label: string }[] = [
  { key: 'search', label: 'Search' },
  { key: 'upload', label: 'Upload SEDI Report' },
];

const InsiderOwnershipPage: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('search');

  return (
    <div style={styles.container}>
      <h4 style={styles.heading}>Insider Ownership (SEDI)</h4>

      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #0d58e6', marginBottom: '0.5em' }}>
        {TABS.map((t) => (
          <span
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '4px 16px',
              cursor: 'pointer',
              backgroundColor: tab === t.key ? '#cce6ff' : '#f0f0f0',
              border: '1px solid #0d58e6',
              borderBottom: tab === t.key ? '2px solid #fff' : '1px solid #0d58e6',
              marginBottom: '-2px',
              fontWeight: tab === t.key ? 'bold' : 'normal',
              fontSize: '0.85em',
            }}
          >
            {t.label}
          </span>
        ))}
      </div>

      {tab === 'search' && <SearchTab />}
      {tab === 'upload' && <UploadTab />}
    </div>
  );
};

/* ---- Search Tab ---- */
interface TickerInfo {
  ticker: string;
  exchange: string;
  name: string;
}

const SearchTab: React.FC = () => {
  const [tickerList, setTickerList] = useState<TickerInfo[]>([]);
  const [data, setData] = useState<SearchData | null>(null);
  const [selectedTicker, setSelectedTicker] = useState('');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    insiderOwnershipApi.tickers()
      .then((res) => setTickerList(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleClick = (ticker: string) => {
    setSelectedTicker(ticker);
    setSearching(true);
    insiderOwnershipApi.search(ticker)
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setSearching(false));
  };

  const handleBack = () => {
    setData(null);
    setSelectedTicker('');
  };

  const fmt = (n: number) => n.toLocaleString();

  const renderGroup = (title: string, items: InsiderHolding[], emptyMsg: string) => (
    <>
      <tr><td style={{ ...styles.td, color: 'green', fontWeight: 'bold', paddingTop: '0.8em' }} colSpan={5}>{title}</td></tr>
      {items.length === 0 ? (
        <tr><td style={{ ...styles.td, color: 'red' }} colSpan={5}>{emptyMsg}</td></tr>
      ) : (
        items.map((h, idx) => (
          <tr key={`${title}-${idx}`} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
            <td style={styles.td}>{h.ticker}</td>
            <td style={styles.td}>{h.insider}</td>
            <td style={styles.td}>{h.relationship}</td>
            <td style={{ ...styles.td, textAlign: 'right' }}>{fmt(h.balance)}</td>
            <td style={styles.td}>{h.designation}</td>
          </tr>
        ))
      )}
    </>
  );

  if (loading) return <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>;

  // Detail view: grouped insider ownership + institutional holdings
  if (data) {
    const info = tickerList.find(t => t.ticker === selectedTicker);
    return (
      <>
        <div style={{ fontSize: '0.85em', marginBottom: '0.5em' }}>
          <span onClick={handleBack} style={{ color: '#0069cc', cursor: 'pointer', textDecoration: 'underline' }}>Back to list</span>
          <span style={{ marginLeft: '1em', fontWeight: 'bold', fontSize: '1.1em' }}>
            {selectedTicker}{info ? ` - ${info.name}` : ''}
            {info?.exchange ? <span style={{ color: '#666', fontWeight: 'normal' }}> ({info.exchange})</span> : ''}
          </span>
        </div>

        {searching ? (
          <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Ticker</th>
                  <th style={styles.th}>Insider</th>
                  <th style={styles.th}>Relationship</th>
                  <th style={styles.th}>Balance</th>
                  <th style={styles.th}>Designation</th>
                </tr>
              </thead>
              <tbody>
                {renderGroup('10+% Owner Holdings', data.per10owners, 'No 10+% owners')}
                {renderGroup('Director Holdings', data.directors, 'Directors hold 0 shares')}
                {renderGroup('Senior Management Holdings', data.officers, 'Senior Officers hold 0 shares')}
                {renderGroup('Issuer Holdings', data.issuer, 'Issuer holds zero shares')}
              </tbody>
            </table>

            <table style={{ ...styles.table, marginTop: '1.5em' }}>
              <thead>
                <tr>
                  <th style={styles.th} colSpan={7}>
                    Institutional Ownership: {fmt(data.totalInstitutionalShares)} shares
                  </th>
                </tr>
                <tr>
                  <th style={styles.th}>Institution</th>
                  <th style={styles.th}>Report Date</th>
                  <th style={styles.th}>Filing Quarter</th>
                  <th style={styles.th}>Name of Issuer</th>
                  <th style={styles.th}>Designation</th>
                  <th style={styles.th}>Shares</th>
                  <th style={styles.th}>Type</th>
                </tr>
              </thead>
              <tbody>
                {data.institutions.length === 0 ? (
                  <tr><td style={{ ...styles.td, color: 'red' }} colSpan={7}>Found Zero Institutional Holders from Edgar</td></tr>
                ) : (
                  data.institutions.map((inst, idx) => (
                    <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
                      <td style={styles.td}>{inst.filerName}</td>
                      <td style={styles.td}>{inst.reportDate || '-'}</td>
                      <td style={styles.td}>{inst.filingQuarter || '-'}</td>
                      <td style={styles.td}>{inst.nameOfIssuer || '-'}</td>
                      <td style={styles.td}>{inst.designation || '-'}</td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>{fmt(inst.shares)}</td>
                      <td style={styles.td}>{inst.type || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </>
        )}
      </>
    );
  }

  // List view: table of all tickers with exchange and company name
  return (
    <table style={styles.table}>
      <thead>
        <tr>
          <th style={styles.th}>Ticker</th>
          <th style={styles.th}>Exchange</th>
          <th style={styles.th}>Company Name</th>
        </tr>
      </thead>
      <tbody>
        {tickerList.length === 0 ? (
          <tr><td style={{ ...styles.td, color: '#999' }} colSpan={3}>No insider ownership data available.</td></tr>
        ) : (
          tickerList.map((t, idx) => (
            <tr key={t.ticker} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff', cursor: 'pointer' }}
              onClick={() => handleClick(t.ticker)}>
              <td style={{ ...styles.td, color: '#0069cc', fontWeight: 'bold' }}>{t.ticker}</td>
              <td style={styles.td}>{t.exchange || '-'}</td>
              <td style={styles.td}>{t.name || '-'}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
};

/* ---- Upload Tab ---- */
const SEDI_URL = 'https://www.sedi.ca/sedi/SVTReportsAccessController?menukey=null&locale=en_CA';

const UploadTab: React.FC = () => {
  const [ticker, setTicker] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [inserting, setInserting] = useState(false);
  const [preview, setPreview] = useState<ParsedHolding[]>([]);
  const [parsed, setParsed] = useState(false);
  const [result, setResult] = useState('');

  const handleLookup = async () => {
    if (!ticker) return;
    setLookingUp(true);
    try {
      const r = await insiderOwnershipApi.lookupName(ticker);
      if (r.data.name) {
        setCompanyName(r.data.name);
      } else {
        setResult(`No company found for ticker "${ticker}"`);
      }
    } catch {
      setResult('Lookup failed.');
    }
    setLookingUp(false);
  };

  const handleUpload = async () => {
    if (!file || !ticker) return;
    setParsing(true);
    setResult('');
    setPreview([]);
    setParsed(false);
    try {
      const r = await insiderOwnershipApi.upload(file, ticker, companyName);
      setPreview(r.data.holdings);
      setParsed(true);
    } catch (e: any) {
      setResult(e.response?.data?.detail || 'Failed to parse file.');
    }
    setParsing(false);
  };

  const handleConfirm = async () => {
    if (!file || !ticker) return;
    if (!window.confirm(`Insert ${preview.length} holding(s) for ${ticker}?\nExisting insider ownership data for this ticker will be replaced.`)) return;
    setInserting(true);
    setResult('');
    try {
      const r = await insiderOwnershipApi.confirm(file, ticker, companyName);
      setResult(`Done! Inserted ${r.data.inserted} insider ownership record(s) for ${ticker}.`);
      setPreview([]);
      setFile(null);
    } catch (e: any) {
      setResult(e.response?.data?.detail || 'Failed to insert holdings.');
    }
    setInserting(false);
  };

  const fmt = (n: number) => n.toLocaleString();

  return (
    <div style={{ marginTop: '0.5em' }}>
      {/* Step 1 */}
      <div style={{ marginBottom: '1em', padding: '10px 14px', backgroundColor: '#f8f9fa', border: '1px solid #ddd', fontSize: '0.85em' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '1em' }}>Step 1: Download SEDI Insider Ownership Report</div>
        <div style={{ display: 'flex', gap: '0.5em', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
          <label style={{ fontWeight: 'bold' }}>Ticker:</label>
          <input value={ticker} onChange={e => { setTicker(e.target.value.toUpperCase()); setCompanyName(''); }}
            onKeyDown={e => e.key === 'Enter' && handleLookup()}
            placeholder="e.g. TD" style={{ ...styles.input, width: '8em' }} />
          <input type="button" value={lookingUp ? '...' : 'Lookup'}
            onClick={handleLookup} disabled={!ticker || lookingUp}
            style={{ ...styles.button, marginLeft: '0.3em' }} />
          <label style={{ fontWeight: 'bold', marginLeft: '1em' }}>Company Name:</label>
          <input value={companyName} onChange={e => setCompanyName(e.target.value)}
            placeholder="auto-filled or enter manually" style={{ ...styles.input, width: '20em' }} />
          <a href={SEDI_URL} target="_blank" rel="noreferrer"
            style={{ ...styles.button, textDecoration: 'none', display: 'inline-block', backgroundColor: '#b3d9ff', fontWeight: 'bold', marginLeft: '1em' }}>
            Open SEDI Website
          </a>
        </div>
        <div style={{ marginBottom: '6px' }}>On the SEDI website:</div>
        <ol style={{ margin: '0 0 0 1.5em', padding: 0, lineHeight: '1.8' }}>
          <li>Select report type: <strong>Insider information by issuer</strong></li>
          <li>Click <strong>Next</strong></li>
          <li>Search for the company by name, select it</li>
          <li>Click <strong>Search</strong></li>
          <li>Download the PDF or save the page as HTML</li>
        </ol>
      </div>

      {/* Step 2 */}
      <div style={{ marginBottom: '1em', padding: '10px 14px', backgroundColor: '#f8f9fa', border: '1px solid #ddd', fontSize: '0.85em' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '1em' }}>Step 2: Upload & Parse</div>
        <div style={{ display: 'flex', gap: '0.5em', alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="file" accept=".html,.htm,.pdf" onChange={e => { setFile(e.target.files?.[0] || null); setPreview([]); setResult(''); setParsed(false); }}
            style={{ fontSize: '0.95em' }} />
          <input type="button" value={parsing ? 'Parsing...' : 'Upload & Parse'}
            onClick={handleUpload} disabled={!file || !ticker || parsing} style={styles.button} />
        </div>
      </div>

      {/* Messages */}
      {result && (
        <div style={{
          fontSize: '0.85em', marginBottom: '0.5em', padding: '6px 12px',
          backgroundColor: result.startsWith('Done') ? '#d4edda' : '#f8d7da',
          borderRadius: '4px', fontWeight: 'bold',
        }}>
          {result}
        </div>
      )}
      {parsed && preview.length === 0 && !result && (
        <div style={{ fontSize: '0.85em', padding: '6px 12px', backgroundColor: '#fff3cd', borderRadius: '4px', marginBottom: '0.5em' }}>
          No holdings found in the uploaded file. Make sure the SEDI search returned results before saving the page.
        </div>
      )}

      {/* Step 3: Preview */}
      {preview.length > 0 && (
        <div style={{ marginBottom: '1em' }}>
          <div style={{ fontSize: '0.85em', marginBottom: '0.5em' }}>
            Parsed <strong>{preview.length}</strong> insider ownership record(s) for <strong>{ticker}</strong>.
          </div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Insider</th>
                <th style={styles.th}>Relationship</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Designation</th>
                <th style={styles.th}>Holder</th>
                <th style={styles.th}>Balance</th>
                <th style={styles.th}>Calculated</th>
                <th style={styles.th}>Equivalent</th>
              </tr>
            </thead>
            <tbody>
              {preview.map((h, idx) => (
                <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
                  <td style={styles.td}>{h.insider}</td>
                  <td style={styles.td}>{h.relationship}</td>
                  <td style={{ ...styles.td, color: h.statusChange !== 'Not Applicable' ? 'red' : undefined }}>{h.statusChange}</td>
                  <td style={styles.td}>{h.date}</td>
                  <td style={styles.td}>{h.designation}</td>
                  <td style={styles.td}>{h.holder}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{fmt(h.balance)}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{fmt(h.calculated)}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{fmt(h.equivalent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: '0.5em' }}>
            <input type="button"
              value={inserting ? 'Inserting...' : `Confirm & Insert ${preview.length} Record(s)`}
              onClick={handleConfirm}
              disabled={inserting}
              style={{ ...styles.button, backgroundColor: '#b3d9ff', fontWeight: 'bold' }} />
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { padding: '1em 0.5em', backgroundColor: '#fff', minHeight: 'calc(100vh - 6em)' },
  heading: { color: '#0069cc', marginTop: '1em', textAlign: 'center' },
  searchBar: { backgroundColor: '#ececec', padding: '8px 12px', fontSize: '0.85em', marginBottom: '1em' },
  input: { padding: '2px 4px', fontFamily: 'Arial, sans-serif', fontSize: '1em', width: '10em' },
  button: { padding: '4px 16px', backgroundColor: '#ccdcfc', color: '#000', border: '1px solid #808080', cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontSize: '1em' },
  table: { width: '100%', marginTop: '0.5em', border: '2px solid #0d58e6', borderCollapse: 'collapse' as const },
  th: { border: '1px solid #0d58e6', padding: '4px 6px', backgroundColor: '#cce6ff', fontWeight: 'bold', fontSize: '0.8em', textAlign: 'center' as const },
  td: { border: '1px solid #0d58e6', padding: '3px 6px', fontSize: '0.8em' },
};

export default InsiderOwnershipPage;
